// useTaskHubSettings.ts

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  TaskHubUserSettings,
  TaskHubQuery,
  TaskHubFilters,
} from './TaskHubSettings.types';
import { TaskHubSettingsService } from './TaskHubSettingsService';
import { getUserMeContactId } from '../../../apps/main/rootReducer';

interface UseTaskHubSettingsReturn {
  settings: TaskHubUserSettings | null;
  savedQueries: TaskHubQuery[];
  defaultQuery: TaskHubQuery | null;
  loading: boolean;
  error: string | null;
  saveQuery: (
    displayName: string,
    filters: TaskHubFilters
  ) => Promise<TaskHubQuery | null>;
  deleteQuery: (queryId: string) => Promise<boolean>;
  setDefaultQuery: (queryId: string) => Promise<boolean>;
  loadQuery: (queryId: string) => TaskHubQuery | null;
  refreshSettings: () => Promise<void>;
}

export const useTaskHubSettings = (): UseTaskHubSettingsReturn => {
  const [settings, setSettings] = useState<TaskHubUserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const contactId = useSelector(getUserMeContactId);
  const settingsServiceRef = useRef<TaskHubSettingsService>(
    new TaskHubSettingsService()
  );

  const handleError = useCallback(
    (error: any, operation: string) => {
      const message =
        error.message || t('taskHub:settings.errors.genericError');
      console.error(`TaskHub Settings ${operation} error:`, error);
      setError(message);
    },
    [t]
  );

  const loadSettings = useCallback(async () => {
    if (!contactId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userSettings =
        await settingsServiceRef.current.loadSettings(contactId);
      setSettings(userSettings);
    } catch (err: any) {
      handleError(err, 'load');
    } finally {
      setLoading(false);
    }
  }, [contactId, handleError]);

  // FIXED: Return the created query and handle errors properly
  const saveQuery = useCallback(
    async (
      displayName: string,
      filters: TaskHubFilters
    ): Promise<TaskHubQuery | null> => {
      if (!contactId) return null;

      try {
        setError(null);
        const newQuery = await settingsServiceRef.current.addQuery(
          contactId,
          displayName,
          filters
        );

        // Refresh settings to get updated state
        const updatedSettings =
          await settingsServiceRef.current.loadSettings(contactId);
        setSettings(updatedSettings);

        return newQuery;
      } catch (err: any) {
        handleError(err, 'save query');
        return null;
      }
    },
    [contactId, handleError]
  );

  const deleteQuery = useCallback(
    async (queryId: string): Promise<boolean> => {
      if (!contactId) return false;

      try {
        setError(null);
        await settingsServiceRef.current.deleteQuery(contactId, queryId);

        // Refresh settings to get updated state
        const updatedSettings =
          await settingsServiceRef.current.loadSettings(contactId);
        setSettings(updatedSettings);

        return true;
      } catch (err: any) {
        handleError(err, 'delete query');
        return false;
      }
    },
    [contactId, handleError]
  );

  const setDefaultQuery = useCallback(
    async (queryId: string): Promise<boolean> => {
      if (!contactId) return false;

      try {
        setError(null);
        await settingsServiceRef.current.setDefaultQuery(contactId, queryId);

        // Refresh settings to get updated state
        const updatedSettings =
          await settingsServiceRef.current.loadSettings(contactId);
        setSettings(updatedSettings);

        return true;
      } catch (err: any) {
        handleError(err, 'set default query');
        return false;
      }
    },
    [contactId, handleError]
  );

  const loadQuery = useCallback((queryId: string): TaskHubQuery | null => {
    return settingsServiceRef.current.findQuery(queryId);
  }, []);

  const refreshSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  // Initial load
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Computed values
  const savedQueries = settings?.taskHubQueries || [];
  const defaultQuery = settingsServiceRef.current.getDefaultQuery();

  return {
    settings,
    savedQueries,
    defaultQuery,
    loading,
    error,
    saveQuery,
    deleteQuery,
    setDefaultQuery,
    loadQuery,
    refreshSettings,
  };
};
